import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  POWER_LEVEL_OPTIONS,
  PERMISSION_EVENTS,
  PERMISSION_LABELS,
  POWER_LEVELS,
} from '../../../../../constants/roomSettings';
import './PermissionsPage.css';

const PermissionRow = ({
  label,
  value,
  onChange,
  canConfigure,
}) => {
  const [customValue, setCustomValue] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const handleChange = (e) => {
    const newValue = parseInt(e.target.value, 10);
    
    if (newValue === POWER_LEVELS.CUSTOM) {
      setShowCustomInput(true);
      setCustomValue(value.toString());
    } else {
      setShowCustomInput(false);
      onChange(newValue);
    }
  };

  const handleCustomSubmit = () => {
    const numValue = parseInt(customValue, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      onChange(numValue);
      setShowCustomInput(false);
    }
  };

  const handleCustomKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCustomSubmit();
    } else if (e.key === 'Escape') {
      setShowCustomInput(false);
      setCustomValue('');
    }
  };

  const handleCustomValueClick = () => {
    if (canConfigure) {
      setShowCustomInput(true);
      setCustomValue(value.toString());
    }
  };

  // Check if current value is a custom (non-standard) value
  const isCustomValue = !POWER_LEVEL_OPTIONS.some(opt => opt.value === value && opt.value !== POWER_LEVELS.CUSTOM);

  // Determine which option should be selected
  const getSelectedValue = () => {
    if (showCustomInput) return POWER_LEVELS.CUSTOM;
    const matchingOption = POWER_LEVEL_OPTIONS.find(opt => opt.value === value && opt.value !== POWER_LEVELS.CUSTOM);
    return matchingOption ? value : POWER_LEVELS.CUSTOM;
  };

  return (
    <div className="permission-row">
      <span className="permission-label">{label}</span>
      <div className="permission-control">
        <select
          className="permission-select"
          value={getSelectedValue()}
          onChange={handleChange}
          disabled={!canConfigure}
        >
          {POWER_LEVEL_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {showCustomInput && (
          <div className="permission-custom-input-container">
            <input
              type="number"
              className="permission-custom-input"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              onKeyDown={handleCustomKeyPress}
              onBlur={handleCustomSubmit}
              placeholder="Enter level (0-100)"
              min="0"
              max="100"
              autoFocus
            />
          </div>
        )}
        {!showCustomInput && isCustomValue && (
          <span 
            className={`permission-custom-value ${canConfigure ? 'permission-custom-value-clickable' : ''}`}
            onClick={handleCustomValueClick}
            title={canConfigure ? 'Click to edit custom value' : ''}
          >
            ({value})
          </span>
        )}
      </div>
    </div>
  );
};

PermissionRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  canConfigure: PropTypes.bool,
};

const PermissionsPage = ({
  permissionLevels = {},
  onPermissionChange = () => {},
  canConfigure = false,
}) => {
  const permissionKeys = Object.keys(PERMISSION_EVENTS);

  return (
    <div className="room-settings-page permissions-page">
      <div className="room-settings-section">
        <h3 className="room-settings-section-title">Role Permissions</h3>
        <p className="room-settings-description">
          Configure the minimum power level required for each action. 
          Default = 0, Moderator = 50, Admin = 100.
        </p>
        <div className="permissions-list">
          {permissionKeys.map((key) => {
            const permissionKey = PERMISSION_EVENTS[key];
            const label = PERMISSION_LABELS[permissionKey];
            const value = permissionLevels[permissionKey] ?? 0;

            return (
              <PermissionRow
                key={permissionKey}
                label={label}
                value={value}
                onChange={(newValue) => onPermissionChange(permissionKey, newValue)}
                canConfigure={canConfigure}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

PermissionsPage.propTypes = {
  permissionLevels: PropTypes.object,
  onPermissionChange: PropTypes.func,
  canConfigure: PropTypes.bool,
};

export default PermissionsPage;
